<template>
  <div>
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="groups-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <ThreePeople :color="iconColor" class="groups-icon" />
              {{ $t('groups.title') }}
            </h1>
          </div>
          <div class="mb-7 mt-12">
            <v-btn
              v-if="!openGroupForm"
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold"
              :disabled="
                isAuthenticated && !checkAuthorization(['superAdmin', 'admin'])
              "
              @click="addNewHandler"
              >{{ $t('groups.buttons.createNewGroup') }}</v-btn
            >
            <div v-else class="close-link" @click="closebuilder">
              <v-icon :icon="'arrowleft'" />
              <span class="close-link-text">{{
                $t('groups.buttons.close')
              }}</span>
            </div>
          </div>
          <v-card v-if="groupList && !openGroupForm" color="transparent" flat>
            <v-card-title
              class="px-0 pb-6 d-flex align-center flex-wrap flex-sm-nowrap"
            >
              <span class="users-action-bar font-weight-bold"
                >{{ $t('groups.totalGroups') }}: {{ groupList.length }}</span
              >
              <v-spacer></v-spacer>
              <v-text-field
                v-model="searchGroups"
                :append-inner-icon="searchGroups ? '' : 'mdi-magnify'"
                :label="$t('groups.searchPlaceholder')"
                single-line
                hide-details
                variant="solo"
                flat
                clearable
                density="compact"
                color="#E8E9EC"
                style="max-width: 265px"
              ></v-text-field>
            </v-card-title>
          </v-card>
          <v-progress-linear
            v-if="isediting"
            indeterminate
            color="#A5D867"
            class="mb-4"
          />
          <v-form
            v-if="openGroupForm && users.length"
            class="mb-12"
            ref="addeditform"
          >
            <v-card class="py-10 px-15">
              <v-card-title class="px-0 font-weight-bold font-size-32">{{
                editingGroup
                  ? $t('groups.forms.editGroup')
                  : $t('groups.forms.groupBuilder')
              }}</v-card-title>
              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >{{ $t('groups.forms.groupName') }}</v-card-text
              >
              <v-text-field
                v-model="groupName"
                variant="outlined"
                :placeholder="$t('groups.forms.enterGroupName')"
                :rules="[rules.required, rules.length(2)]"
                class="user-input mt-1"
                color="#A0C548"
                :loading="isediting"
                style="min-width: 100%"
              ></v-text-field>

              <v-card-text
                class="pa-0 pt-2 mb-1 text-subtitle-1 font-weight-bold text-uppercase"
                >{{ $t('groups.forms.leader') }}</v-card-text
              >
              <template
                v-if="
                  editingGroup
                    ? checkAuthorization(['superAdmin'])
                    : checkAuthorization(['superAdmin', 'admin'])
                "
              >
                <v-btn
                  @click="leaderDialog = true"
                  variant="flat"
                  block
                  color="#A5D867"
                >
                  {{ $t('groups.buttons.selectLeader') }}
                </v-btn>

                <div v-if="leader" class="mt-2 mb-4 d-flex align-center">
                  <v-avatar size="35" class="mr-2">
                    <template v-if="leader.url || leader.raw?.url">
                      <v-img :src="leader.url || leader.raw.url" />
                    </template>
                    <template v-else>
                      <v-avatar color="#cfebae">
                        <span>{{
                          $helpers.shortHand(
                            leader.user_name || leader.raw?.user_name || ''
                          )
                        }}</span>
                      </v-avatar>
                    </template>
                  </v-avatar>
                  <span>{{
                    leader.user_name || leader.raw?.user_name || 'Unnamed'
                  }}</span>
                </div>
              </template>

              <template v-else>
                <div class="pb-6">
                  <v-chip color="#A0C548" variant="flat">
                    <v-avatar v-if="leader.url" start>
                      <v-img :src="leader.url"></v-img>
                    </v-avatar>
                    <v-avatar color="#cfebae" v-else start>
                      <span>{{ $helpers.shortHand(leader.user_name) }}</span>
                    </v-avatar>
                    {{ leader.user_name }}
                  </v-chip>
                </div>
              </template>

              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >{{ $t('groups.forms.members') }}
                <span class="text-caption"
                  >[{{ $t('groups.forms.total') }}:
                  {{ filteredItems.length }}]</span
                ></v-card-text
              >
              <v-card border elevation="0" class="pa-5">
                <v-layout wrap class="mb-5">
                  <div class="letter-filter-container">
                    <span class="font-weight-bold"
                      >{{ $t('groups.forms.filter') }}
                    </span>
                    <v-hover>
                      <template v-slot="{ hover }">
                        <v-icon
                          v-if="!isHelpDesk()"
                          size="small"
                          class="clear-filter"
                          :color="`${hover ? 'black' : ''}`"
                          @click="filterlist('', '')"
                          >mdi-filter-off</v-icon
                        >
                      </template>
                    </v-hover>
                    <ul>
                      <li v-for="(letter, i) in filters" :key="i">
                        <span
                          class="filter-letter"
                          :class="{ active: activeindex === i }"
                          @click="filterlist(letter, i)"
                          >{{ letter }}</span
                        >
                      </li>
                    </ul>
                  </div>
                  <v-spacer></v-spacer>
                  <div class="search-container">
                    <v-text-field
                      v-model="search"
                      color="#E8E9EC"
                      class="search-field"
                      variant="outlined"
                      flat
                      clearable
                      density="compact"
                      :label="$t('groups.searchPlaceholder')"
                      :append-inner-icon="search ? '' : 'mdi-magnify'"
                      single-line
                      hide-details
                      @keydown="resetPagination"
                    ></v-text-field>
                  </div>
                </v-layout>
                <v-row class="member-list-container">
                  <v-col
                    v-for="user in displayMembers"
                    :key="user.uid"
                    cols="12"
                    sm="6"
                    md="4"
                    class="pa-0"
                  >
                    <v-checkbox
                      class="mt-0 pa-0"
                      multiple
                      false-icon="mdi-radiobox-blank"
                      true-icon="mdi-radiobox-marked"
                      :id="user.uid"
                      v-model="selectedMemberUids"
                      :label="user.user_name"
                      color="#000000"
                      density="compact"
                      :disabled="user.disabled || user.tempdisabled"
                      :value="user.uid"
                    >
                      <template #label>
                        <v-avatar v-if="user.url" start size="27">
                          <v-img :src="user.url"></v-img>
                        </v-avatar>
                        <v-avatar color="#cfebae" v-else start size="27">
                          <span>{{ $helpers.shortHand(user.user_name) }}</span>
                        </v-avatar>
                        <span class="ml-2">{{ user.user_name }}</span>
                      </template>
                    </v-checkbox>
                  </v-col>
                </v-row>
                <v-pagination
                  v-model="currPage"
                  :length="Math.ceil(filteredItems.length / pageSize)"
                  rounded
                  density="compact"
                  active-color="#a5d867"
                  color="#000000"
                  :total-visible="7"
                ></v-pagination>
              </v-card>
              <!-- Members of your Group -->
              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
              >
                {{ $t('groups.forms.members') }}
              </v-card-text>
              <v-card border elevation="0" class="pa-5">
                <v-row dense>
                  <v-col
                    v-for="user in membersOfGroup"
                    :key="user.uid"
                    cols="12"
                    sm="6"
                    md="4"
                    class="d-flex align-center mb-2"
                  >
                    <v-avatar v-if="user.url" start size="27">
                      <v-img :src="user.url" />
                    </v-avatar>
                    <v-avatar color="#cfebae" v-else start size="27">
                      <span>{{ $helpers.shortHand(user.user_name) }}</span>
                    </v-avatar>
                    <span class="ml-2">{{ user.user_name }}</span>
                  </v-col>
                </v-row>
              </v-card>

              <!-- Pending Members -->
              <template v-if="pendingusers.length > 0">
                <v-card-text
                  class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >
                  {{ $t('groups.forms.pendingMembers') }}
                </v-card-text>

                <v-card border elevation="0" class="pa-5">
                  <v-row dense>
                    <v-col
                      v-for="(user, i) in thisGroupsPendingUsers"
                      :key="i"
                      cols="12"
                      sm="6"
                      md="4"
                      class="d-flex flex-column mb-2"
                    >
                      <div class="mb-2">{{ user.email }}</div>

                      <div class="d-flex align-center">
                        <v-btn
                          class="text-white font-weight-bold text-subtitle-2"
                          size="small"
                          variant="flat"
                          color="#A5D867"
                          :loading="loading && i === index"
                          @click="
                            () => {
                              reinviteMember(user.email)
                              index = i
                            }
                          "
                        >
                          Resend Invite
                          <template #loader>
                            <span>Sending...</span>
                            <span class="custom-loader">
                              <v-icon>cached</v-icon>
                            </span>
                          </template>
                        </v-btn>

                        <v-icon
                          class="ml-2"
                          @click="
                            () => {
                              confirmDialog = true
                              userToRemove = user.email
                            }
                          "
                        >
                          $delete_icon
                        </v-icon>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <v-card-text
                class="pa-0 pt-2 mt-3 text-subtitle-1 font-weight-bold text-uppercase"
              >
                {{ $t('groups.forms.inviteMember') }}
                <span class="text-caption text-red font-weight-bold">
                  {{ $t('groups.forms.onlyOneEmail') }}
                </span>
              </v-card-text>

              <v-form ref="form" v-model="valid" @submit.prevent>
                <v-text-field
                  v-model="email"
                  class="invite-email-field"
                  style="min-width: 100%; width: 100%"
                  variant="outlined"
                  clearable
                  type="email"
                  name="email"
                  :placeholder="$t('groups.forms.emailAddress')"
                  :loading="loading2"
                  :rules="[emailRules.format, emailRules.whitespace]"
                  @keydown.enter="inviteMember"
                />
              </v-form>

              <v-btn
                class="text-white font-weight-bold text-subtitle-2 mx-auto my-3"
                variant="flat"
                color="#A5D867"
                :loading="loading2"
                :disabled="!valid || loading2"
                @click="inviteMember"
              >
                {{ $t('groups.buttons.sendInvite') }}
                <template #loader>
                  <div class="d-flex align-center">
                    <v-progress-circular
                      indeterminate
                      size="18"
                      width="2"
                      color="white"
                      class="mr-2"
                    />
                    <span>Sending...</span>
                  </div>
                </template>
              </v-btn>

              <v-alert
                v-if="feedback"
                border="start"
                color="#592892"
                class="text-center mt-6"
              >
                {{ feedback }}
              </v-alert>
            </v-card>
            <div class="text-center">
              <v-btn
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold my-14 px-14"
                :disabled="isSavingGroup"
                :loading="isSavingGroup"
                @click="editingGroup ? editGroup() : addGroup()"
                >{{
                  editingGroup
                    ? $t('groups.buttons.save')
                    : $t('groups.buttons.add')
                }}</v-btn
              >
            </div>
            <v-progress-linear
              :indeterminate="isSavingGroup"
              :active="isSavingGroup"
              color="#592892"
            ></v-progress-linear>
            <v-divider class="dashed"></v-divider>
          </v-form>
          <v-data-table
            :headers="translatedHeaders"
            fixed-header
            :loading="isloading"
            :loading-text="$t('groups.messages.loadingGroups')"
            :no-data-text="$t('groups.messages.noGroupsForChurch')"
            :items="groupList"
            :items-per-page="20"
            :header-props="{ sortIcon: '$sortarrowdown' }"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, 40, 50, -1],
            }"
            item-key="group_doc_id"
            class="elevation-1 users-table"
            :search="searchGroups"
          >
            <template #progress>
              <v-progress-linear
                color="#592892"
                indeterminate
              ></v-progress-linear>
            </template>

            <template #[`item.members`]="{ item }">
              <div
                v-if="item.members.length"
                class="edit-members-dialog-container"
              >
                {{ item.members.length }}
              </div>
              <div v-else>
                <span
                  v-if="
                    checkAuthorization(['superAdmin', 'admin']) &&
                    !isHelpDesk()
                  "
                >
                  <v-btn
                    variant="flat"
                    size="small"
                    class="add-members-link"
                    @click="loadGroupToEdit(item)"
                    >{{ $t('groups.buttons.addMembers') }}</v-btn
                  >
                </span>
                <span v-else>{{ item.members.length }}</span>
              </div>
            </template>
            <template
              v-if="
                isAuthenticated &&
                checkAuthorization(['superAdmin', 'admin', 'leader'])
              "
              #[`item.actions`]="{ item }"
            >
              <div class="action-icons">
                <v-icon
                  v-if="
                    !isHelpDesk() &&
                    (checkIfLeader(item) ||
                      checkAuthorization(['superAdmin', 'admin']))
                  "
                  @click.native="loadGroupToEdit(item)"
                  class="mr-6"
                  >edit</v-icon
                >
                <v-tooltip
                  location="bottom"
                  :disabled="item.hasCourse == undefined"
                >
                  <template #activator="{ props }">
                    <!-- Delete Icon -->
                    <span v-bind="props">
                      <v-icon
                        v-if="
                          checkAuthorization(['superAdmin', 'admin']) &&
                          !isHelpDesk()
                        "
                        @click.native="deleteItem(item, 'groups')"
                        :disabled="item.hasCourse != undefined"
                        >delete_icon</v-icon
                      >
                    </span>
                  </template>
                  <span
                    >This group may not be removed until its course has been
                    archived</span
                  >
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-flex>
      <!-- Dialogs -->
      <v-dialog v-model="confirmDialog" persistent max-width="400px">
        <v-card
          :color="$vuetify.theme.dark ? '#2d2d2d' : 'rgba(255,255,255,.95)'"
        >
          <v-card-title class="text-h6 pb-0 justify-center"
            >Remove Member Invite</v-card-title
          >
          <v-card-text class="text-center"
            >Are you sure you want to remove
            {{ this.userToRemove }}?</v-card-text
          >
          <v-card-actions class="justify-center pb-4">
            <v-btn
              class="font-weight-bold px-2"
              color="#a5d867"
              :loading="loading3"
              @click="removeMemberInvite"
            >
              Yes, Remove
              <span slot="loader">Removing</span>
              <span slot="loader" class="custom-loader">
                <v-icon>cached</v-icon>
              </span>
            </v-btn>
            <v-btn color="#383935" @click="confirmDialog = false"
              >No, Never Mind</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- Leader Dialogs -->
      <v-dialog v-model="leaderDialog" max-width="900">
        <v-card>
          <v-card-title
            class="v-card-text pb-0 text-subtitle-1 font-weight-bold text-uppercase"
            >{{ $t('groups.dialogs.selectLeader.title') }}</v-card-title
          >

          <!-- FIXED FILTER + SEARCH -->
          <div class="dialog-header-controls">
            <!-- Put your filters + search bar here -->
            <!-- Example: -->
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div class="letter-filter-container d-flex align-center">
                <span class="font-weight-bold mr-2">{{
                  $t('groups.forms.filter')
                }}</span>
                <span
                  v-for="(letter, i) in filters"
                  :key="i"
                  :class="{ active: activeindex === i }"
                  class="filter-letter mx-1"
                  @click="filterlist(letter, i)"
                >
                  {{ letter }}
                </span>
              </div>
              <v-text-field
                v-model="search"
                :label="$t('groups.searchPlaceholder')"
                variant="outlined"
                density="compact"
                class="ml-auto"
                style="max-width: 200px"
                hide-details
                clearable
                :append-inner-icon="search ? '' : 'mdi-magnify'"
                single-line
                flat
                @keydown="resetPagination"
              />
            </div>
          </div>

          <!-- SCROLLABLE USER LIST -->
          <v-card-text class="dialog-scroll-list">
            <v-row>
              <v-col
                v-for="user in displayMembers"
                :key="user.uid"
                cols="12"
                md="6"
              >
                <v-card
                  :elevation="
                    !user.disabled && !user.tempdisabled &&
                    selectedLeader?.uid === user.uid
                      ? 4
                      : 1
                  "
                  :class="[
                    'd-flex align-center pa-2 leader-option',
                    {
                      'leader-option--disabled':
                        user.disabled || user.tempdisabled,
                      'leader-option--selected':
                        !user.disabled &&
                        !user.tempdisabled &&
                        selectedLeader?.uid === user.uid,
                    },
                  ]"
                  :color="
                    !user.disabled &&
                    !user.tempdisabled &&
                    selectedLeader?.uid === user.uid
                      ? '#eef9e0'
                      : ''
                  "
                  @click="selectLeaderCandidate(user)"
                >
                  <v-avatar
                    size="35"
                    class="mr-2"
                    :color="
                      !(user.url || user.raw?.url) ? '#a5d867' : undefined
                    "
                  >
                    <template v-if="user.url || user.raw?.url">
                      <v-img :src="user.url || user.raw.url" />
                    </template>
                    <template v-else>
                      <span>{{
                        $helpers.shortHand(
                          user.user_name || user.raw?.user_name || ''
                        )
                      }}</span>
                    </template>
                  </v-avatar>
                  <span>{{
                    user.user_name || user.raw?.user_name || 'Unnamed'
                  }}</span>
                  <span
                    v-if="user.disabled && !user.tempdisabled"
                    class="text-caption text-medium-emphasis ml-2"
                  >
                    ({{
                      $t(
                        user.disabledReason === 'member'
                          ? 'groups.messages.alreadyMemberOfGroup'
                          : 'groups.messages.alreadyLeadsGroup'
                      )
                    }})
                  </span>
                  <v-spacer />
                  <v-icon
                    v-if="
                      !user.disabled &&
                      !user.tempdisabled &&
                      selectedLeader?.uid === user.uid
                    "
                    >mdi-check-circle</v-icon
                  >
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- ACTIONS: pagination + buttons -->
          <v-card-actions class="dialog-actions">
            <v-pagination
              v-model="currPage"
              :length="Math.ceil(filteredItems.length / pageSize)"
              rounded
              density="compact"
              active-color="#a5d867"
              color="#000000"
              class="mr-auto"
              :total-visible="5"
            />
            <v-btn variant="text" @click="leaderDialog = false">{{
              $t('groups.dialogs.selectLeader.cancel')
            }}</v-btn>
            <v-btn
              color="#A5D867"
              variant="flat"
              class="elevation-0 font-weight-bold text-white"
              :disabled="
                !selectedLeader ||
                selectedLeader.disabled ||
                selectedLeader.tempdisabled
              "
              @click="confirmLeader"
            >
              {{ $t('groups.dialogs.selectLeader.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
  import firebase from 'firebase'
  import db from '@/firebase/init'
  import { scopeToChurch } from '@/helpers/churchScope'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  // import moment from 'moment'
  import { mapGetters } from 'vuex'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import ThreePeople from '@/assets/icons/ThreePeople.vue'
  import { currentTheme } from '@/plugins/vuetify'
  import { isValidEmailAddress } from '@/helpers/isValidEmailAddress'
  import { matchesConfirmWord } from '@/helpers/confirmWord'
  import {
    evaluateGroupAssignments,
    loadChurchGroups,
  } from '@/helpers/groupAssignmentRules'

  export default {
    name: 'Users',
    data() {
      return {
        feedback: null,
        backdoor: 0,
        groups: [],
        allMembers: [],
        groupName: '',
        users: [],
        courses: [],
        userToRemove: null,
        pendingusers: [],
        expanded: [],
        currPage: 1,
        pageSize: 30,
        openGroupForm: false,
        singleExpand: true,
        isloading: true,
        isFormValid: false,
        isediting: false,
        confirmDialog: false,
        activeindex: null,
        search: '',
        searchGroups: '',
        searchUsers: '',
        filtered: '',
        leader: null,
        loader: null,
        loading: false,
        loading2: false,
        loading3: false,
        index: -1,
        email: '',
        valid: false,
        currentLeader: '',
        selectedMemberUids: [],
        // displayMembers: [],
        tempdisabled: false,
        editMembersDialog: false,
        editingGroup: false,
        isEditingGroup: false,
        isSavingGroup: false,
        groupToEdit: null,
        usersLoaded: false,
        leaderDialog: false,
        selectedLeader: null,
        headers: [],
        memberheaders: [],
        rules: {
          length: (len) => (v) =>
            (v || '').length >= len || `min ${len} characters required `,
          required: (v) => !!v || 'This field is required',
        },
        emailRules: {
          // v => !!v || 'E-mail is required',
          whitespace: (v) =>
            /^([a-zA-Z0-9._%+-]{3,}@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,})+$/.test(
              v
            ) ||
            'There can not be any spaces. Only one email at a time can be sent',
          format: (v) =>
            isValidEmailAddress(v) || 'E-mail must be valid',
        },
        filters: [
          'A',
          'B',
          'C',
          'D',
          'E',
          'F',
          'G',
          'H',
          'I',
          'J',
          'K',
          'L',
          'M',
          'N',
          'O',
          'P',
          'Q',
          'R',
          'S',
          'T',
          'U',
          'V',
          'W',
          'X',
          'Y',
          'Z',
        ],
      }
    },
    components: {
      HowToUseThisPage,
      ThreePeople,
    },
    computed: {
      translatedHeaders() {
        const nameSortRaw = (a, b) => {
          const na = (a.name ?? '').toString().trim()
          const nb = (b.name ?? '').toString().trim()
          return na.localeCompare(nb, undefined, { sensitivity: 'base' })
        }
        const leaderSortRaw = (a, b) => {
          const na = (a.user_name ?? '').toString().trim()
          const nb = (b.user_name ?? '').toString().trim()
          return na.localeCompare(nb, undefined, { sensitivity: 'base' })
        }
        return [
          {
            title: this.$t('groups.tableHeaders.groups'),
            sortable: true,
            key: 'name',
            sortRaw: nameSortRaw,
          },
          {
            title: this.$t('groups.tableHeaders.leader'),
            sortable: true,
            key: 'user_name',
            sortRaw: leaderSortRaw,
          },
          {
            title: this.$t('groups.tableHeaders.members'),
            sortable: false,
            key: 'members',
          },
          {
            title: this.$t('groups.tableHeaders.actions'),
            sortable: false,
            key: 'actions',
          },
        ]
      },
      translatedMemberHeaders() {
        return [
          {
            title: this.$t('groups.tableHeaders.member'),
            sortable: true,
            key: 'user_name',
          },
        ]
      },
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      ...mapGetters('UserData', ['isAuthenticated']),
      groupList() {
        const user = this.$store.getters['UserData/getUser']
        const isAdmin = this.checkAuthorization(['superAdmin', 'admin'])

        // Early return if no groups
        if (!this.groups || this.groups.length === 0) return []

        // Create userMap without side effects
        const userMap = new Map(this.users.map((u) => [u.uid, u]))

        return this.groups
          .filter((group) => isAdmin || (user && user.uid === group.leader))
          .map((group) => {
            const leaderUser = userMap.get(group.leader) || {}
            return {
              ...group,
              ...leaderUser,
              // User docs often include `name` / other keys that collide with the group;
              // sorting and the Groups column must use the group's title from Firestore.
              name: group.name,
            }
          })
      },
      leaderList() {
        if (!this.usersLoaded || !Array.isArray(this.users)) return []

        const editingGroupId =
          this.editingGroup && this.groupToEdit
            ? this.groupToEdit.group_doc_id
            : null

        return this.users
          .filter((item) => {
            return (
              !item.roles.includes('superAdmin') &&
              !item.roles.includes('helpDesk') &&
              !item.roles.includes('admin')
            )
          })
          .map((item) => {
            // Create a copy to avoid mutating original data
            const userCopy = { ...item }

            const isLeader = this.groups.some(
              (g) =>
                g.leader === item.uid && g.group_doc_id !== editingGroupId
            )
            const isMemberOfAnotherGroup = this.groups.some(
              (g) =>
                g.group_doc_id !== editingGroupId &&
                Array.isArray(g.members) &&
                g.members.includes(item.uid)
            )

            userCopy.disabled = isLeader || isMemberOfAnotherGroup
            userCopy.disabledReason = isLeader
              ? 'leader'
              : isMemberOfAnotherGroup
                ? 'member'
                : null
            userCopy.user_name = item.user_name || 'Unnamed'

            return userCopy
          })
      },
      shortHand() {
        return this.$helpers.shortHand
      },
      filteredItems() {
        return this.leaderList
          .filter((item) => {
            const name = this.$helpers.shortHand(item.user_name)
            if (this.search) {
              return (
                item.user_name
                  ?.toLowerCase()
                  .includes(this.search.toLowerCase()) ||
                item.email?.toLowerCase().includes(this.search.toLowerCase())
              )
            }
            return name.toLowerCase().includes(this.filtered)
          })
          .map((item) => ({
            ...item,
            tempdisabled: !!(
              this.leader &&
              this.leader.uid === item.uid
            ),
          }))
      },
      displayMembers() {
        const trimStart = (this.currPage - 1) * this.pageSize
        const trimEnd = trimStart + this.pageSize
        return this.filteredItems.slice(trimStart, trimEnd)
      },
      membersOfGroup() {
        const selected = Array.isArray(this.selectedMemberUids)
          ? [...new Set(this.selectedMemberUids)]
          : []
        const userMap = new Map(this.users.map((u) => [u.uid, u]))
        return selected.map((uid) => userMap.get(uid)).filter(Boolean)
      },
      thisGroupsPendingUsers() {
        return this.pendingusers.filter((item) => {
          if (this.groupToEdit && item.groupID == this.groupToEdit.group_doc_id)
            return item
        })
      },
    },
    watch: {
      search() {
        this.currPage = 1
      },
      churchid: {
        deep: true,
        handler(event) {
          this.init(event)
        },
      },
      loader() {
        const l = this.loader
        this[l] = !this[l]
        this.loader = null
      },
      usersLoaded(val) {
        if (!this.isEditMode) return
        if (val && this.leader) {
          const matched = this.leaderList.find((u) => u.uid === this.leader.uid)
          if (matched) {
            this.leader = matched
          }
        }
      },
      leaderDialog(val) {
        if (!val) {
          this.search = ''
          this.filtered = ''
          this.activeindex = null
          this.currPage = 1
        }
      },
    },
    mounted() {
      this.$nextTick(() => {
        // Small timeout ensures DOM is stable
        setTimeout(() => {
          const ac = this.$refs.leaderAutocomplete
          if (ac) {
            ac.isMenuActive = true
          } else {
            console.warn('❌ leaderAutocomplete ref not found')
          }
        }, 100)
      })
    },
    methods: {
      addNewHandler() {
        if (!this.users.length) {
          this.feedback = 'Please wait — loading user data...'
          return
        }

        const opening = this.editingGroup || !this.openGroupForm
        this.resetGroupFormFields()
        if (this.$refs.addeditform) this.$refs.addeditform.reset()
        this.openGroupForm = opening
      },
      // removeMemberHandler() {
      //     console.log(this.userToRemove)
      //     // this.userToRemove = email
      //     this.confirmDialog = true
      // },
      confirmLeader() {
        if (
          !this.selectedLeader ||
          this.selectedLeader.disabled ||
          this.selectedLeader.tempdisabled
        ) {
          this.feedback =
            this.selectedLeader?.disabledReason === 'member'
              ? this.$t('groups.messages.alreadyMemberOfGroup')
              : this.$t('groups.messages.alreadyLeadsGroup')
          return
        }
        this.leader = this.selectedLeader
        this.leaderDialog = false
      },
      selectLeaderCandidate(user) {
        if (!user || user.disabled || user.tempdisabled) return
        this.selectedLeader = user
      },
      isLeaderOfAnotherGroup(uid, excludeGroupId = null) {
        return this.groupList.some(
          (g) => g.leader === uid && g.group_doc_id !== excludeGroupId
        )
      },
      async leaderAlreadyAssigned(leaderUid, excludeGroupId = null) {
        const cid = this.$store.getters['AppData/cid']
        const snap = await db
          .collection('groups')
          .where('cid', '==', cid)
          .where('leader', '==', leaderUid)
          .get()

        if (!excludeGroupId) {
          return !snap.empty
        }

        return snap.docs.some((doc) => doc.id !== excludeGroupId)
      },
      resetGroupFormFields() {
        this.feedback = ''
        this.backdoor++
        this.leader = null
        this.selectedLeader = null
        this.selectedMemberUids = []
        this.groupName = ''
        this.groupToEdit = null
        this.editingGroup = false
        this.email = ''
      },
      closebuilder() {
        this.resetGroupFormFields()
        this.openGroupForm = false
      },
      getLeader(id) {
        const leader = this.users.filter((x) => x.uid === id)
        return leader
      },
      filterlist(letter, i) {
        this.activeindex = i
        this.filtered = letter.toLowerCase()
        this.currPage = 1
      },
      resetPagination() {
        this.currPage = 1
      },
      async inviteMember() {
        this.loader = 'loading2'
        this.feedback = ''

        if (!this.groupToEdit?.group_doc_id) {
          this.feedback = this.$t('groups.messages.saveGroupBeforeInvite')
          this.loader = null
          this.loading2 = false
          return
        }

        // check to see if the user already exists (in any church; rules only
        // let leaders read their own church's users, so a function checks)
        let userExists
        try {
          const res = await firebase
            .app()
            .functions('us-central1')
            .httpsCallable('userEmailExists')({ email: this.email })
          userExists = res.data.exists === true
        } catch (err) {
          console.error('Failed to check invite email:', err)
          this.feedback = err.message
          this.loader = null
          this.loading2 = false
          return
        }
        // If the user does not exists
        if (!userExists) {
          // we set a delay so the user sees something happen when they click the button
          setTimeout(() => {
            if (this.$refs.form.validate()) {
              this.$store.dispatch('UserData/inviteUserWithEmailLink', {
                email: this.email,
                cid: this.$store.getters['AppData/cid'],
                groupID: this.groupToEdit.group_doc_id,
              })
            } else {
              this.loader = null
              this.loading2 = false
            }
          }, 1000)
        } else {
          // the user exists
          setTimeout(() => {
            this.feedback = 'User already exists in the system.'
            this.loader = null
            this.loading2 = false
            this.$refs.form.reset()
          }, 1000)
        }
      },
      async reinviteMember(email) {
        this.loading = true
        this.feedback = ''

        if (!this.groupToEdit?.group_doc_id) {
          this.feedback = this.$t('groups.messages.saveGroupBeforeInvite')
          this.loading = false
          return
        }

        let pendingRef = scopeToChurch(
          db.collection('pendingusers').where('email', '==', email),
          this.$store
        )
        let isPending = await pendingRef.get()
        for (const doc of isPending.docs) {
          // console.log('1')
          await db.collection('pendingusers').doc(doc.id).delete()
        }
        // we set a delay so the user sees something happen when they click the button
        // setTimeout(() => {
        // console.log('2')
        this.$store.dispatch('UserData/inviteUserWithEmailLink', {
          email: email,
          cid: this.$store.getters['AppData/cid'],
          groupID: this.groupToEdit.group_doc_id,
        })

        // }, 1000)
        // console.log('3')
        // console.log(email, i, this.loading)
      },
      async removeMemberInvite() {
        this.loading3 = true
        this.feedback = ''

        let pendingRef = scopeToChurch(
          db.collection('pendingusers').where('email', '==', this.userToRemove),
          this.$store
        )
        let isPending = await pendingRef.get()
        for (const doc of isPending.docs) {
          await db.collection('pendingusers').doc(doc.id).delete()
        }

        setTimeout(() => {
          // reset the loader
          this.loader = null
          this.loading3 = false
          // close the dialog
          this.confirmDialog = false

          this.$refs.form.reset()
          // refresh the pending screen
          this.getPendingUsers()
        }, 1000)
      },
      async loadGroupToEdit(group) {
        this.isediting = true
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })

        if (
          this.editingGroup &&
          this.groupToEdit &&
          this.groupToEdit.name === group.name
        ) {
          this.resetGroupFormFields()
          this.openGroupForm = false
          this.isediting = false
          return
        }

        this.editingGroup = true
        this.groupToEdit = group
        this.selectedMemberUids = []
        this.currentLeader = group

        const userMap = new Map(this.users.map((u) => [u.uid, u]))

        for (const user of this.users) {
          user.checked = false
          user.disabled =
            this.groupList.some((g) => g.leader === user.uid) ||
            this.allMembers.includes(user.uid)
        }

        // for (const memberID of this.groupToEdit.members) {
        //   const user = userMap.get(memberID)
        //   if (user) {
        //     user.checked = true
        //     user.disabled = false
        //     this.members.push(user)
        //   }
        // }
        const existingMembers = Array.isArray(this.groupToEdit.members)
          ? [...new Set(this.groupToEdit.members)]
          : []
        this.selectedMemberUids = existingMembers.filter((uid) => {
          const leadsOther = this.groups.some(
            (g) =>
              g.leader === uid && g.group_doc_id !== this.groupToEdit.group_doc_id
          )
          const memberOfOther = this.groups.some(
            (g) =>
              g.group_doc_id !== this.groupToEdit.group_doc_id &&
              Array.isArray(g.members) &&
              g.members.includes(uid)
          )
          return !leadsOther && !memberOfOther
        })

        this.leader = userMap.get(group.leader) || ''
        this.groupName = group.name
        this.openGroupForm = true
        this.isediting = false
      },
      async editGroup() {
        if (this.isSavingGroup) return

        if (!(this.leader && this.groupName && this.groupToEdit)) {
          this.feedback = 'There was an error. '

          if (!this.leader) this.feedback += 'You are missing a leader. '
          if (!this.groupName) this.feedback += 'You are missing a group name. '
          if (!this.groupToEdit) this.feedback += 'There was no group to edit. '

          return
        }

        this.isSavingGroup = true
        this.isEditingGroup = true
        this.feedback = ''

        try {
          const selectedMembers = Array.isArray(this.selectedMemberUids)
            ? [...new Set(this.selectedMemberUids)]
            : []
          const validated = await this.validateGroupAssignments(
            this.leader.uid,
            selectedMembers,
            this.groupToEdit.group_doc_id
          )
          if (!validated.ok) {
            this.feedback = validated.message
            return
          }

          // The leader belongs to the group through `leader`, not `members`.
          // A replaced leader stays on as a member; untick them to remove them.
          const previousLeader = this.groupToEdit.leader
          const members = validated.members.filter(
            (uid) => uid !== this.leader.uid
          )
          if (
            previousLeader &&
            previousLeader !== this.leader.uid &&
            !members.includes(previousLeader)
          ) {
            members.push(previousLeader)
          }

          await db.collection('groups').doc(this.groupToEdit.group_doc_id).update({
            leader: this.leader.uid,
            members,
            name: this.groupName,
          })

          this.setGroupLeaderRole(this.leader.uid, this.groupToEdit.leader)
          this.resetGroupFormFields()
          this.openGroupForm = false
          this.init(this.$store.getters['AppData/cid'])
        } catch (error) {
          console.error('Error updating group:', error)
          this.feedback = this.$t('groups.messages.groupError')
        } finally {
          this.isSavingGroup = false
          this.isEditingGroup = false
        }
      },
      async deleteItem(item, collection) {
        if (this.isHelpDesk && this.isHelpDesk()) {
          const key = 'groups.messages.deleteNotAllowed'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete records.'
          return
        }

        if (item && collection) {
          // const hasGroup = this.checkForGroup(item.group_doc_id)
          // console.log(hasGroup)

          let title = ''
          if (collection === 'groups') {
            title = item.name
          }

          const confirmed = await confirmAction({
            title: `${this.$t('groups.dialogs.delete.title')} <br>"${title}"`,
            html: this.$t('groups.dialogs.delete.html'),
            input: 'text',
            inputPlaceholder: this.$t('groups.dialogs.delete.inputPlaceholder'),
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (
                  matchesConfirmWord(
                    value,
                    'DELETE',
                    this.$t('groups.dialogs.delete.inputPlaceholder')
                  )
                ) {
                  resolve()
                } else {
                  resolve(this.$t('groups.dialogs.delete.inputValidator'))
                }
              })
            },
            confirmButtonText: this.$t('groups.dialogs.delete.confirmButton'),
            cancelButtonText: this.$t('groups.dialogs.delete.cancelButton'),
            context: this,
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })

          if (confirmed) {
            await db.collection(collection).doc(item.group_doc_id).delete()
            this.removeGroupLeader(item.leader)
            this.init(this.$store.getters['AppData/cid'])
          }
        } else {
          Swal.fire({
            title: this.$t('groups.messages.errorTitle'),
            text: this.$t('groups.messages.errorDelete'),
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
        }
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const normalizedPermissions = permissions.includes('superAdmin')
            ? [...permissions, 'helpDesk']
            : permissions

          const hasRole = normalizedPermissions.filter(() =>
            userRoles.some((role) => normalizedPermissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      isHelpDesk() {
        const userRoles = this.$store.getters['UserData/getRoles']
        return Array.isArray(userRoles) && userRoles.includes('helpDesk')
      },
      checkIfLeader(item) {
        const getUser = this.$store.getters['UserData/getUser']
        if (getUser && getUser.uid === item.leader) return true

        return false
      },
      checkForGroup(groupID) {
        const groupName = this.courses.filter((item) => {
          if (item.selectedGroup.length > 0) {
            // console.log(item.selectedGroup[0].id === groupID)
            return item.selectedGroup[0].id === groupID
          }
        })

        if (groupName.length > 0) return true

        return false
      },
      async validateGroupAssignments(leaderUid, memberUids, excludeGroupId = null) {
        const cid = this.$store.getters['AppData/cid']
        const churchGroups = await loadChurchGroups(db, cid)
        const result = evaluateGroupAssignments(churchGroups, {
          leaderUid,
          memberUids,
          excludeGroupId,
        })

        if (result.leaderLeadsOther) {
          return {
            ok: false,
            message: this.$t('groups.messages.alreadyLeadsGroup'),
          }
        }
        if (result.leaderIsMemberElsewhere) {
          return {
            ok: false,
            message: this.$t('groups.messages.alreadyMemberOfGroup'),
          }
        }

        const blocked = new Set(result.blockedMemberUids)
        return {
          ok: true,
          members: memberUids.filter((uid) => !blocked.has(uid)),
          omitted: blocked.size > 0,
        }
      },
      async addGroup() {
        if (this.isSavingGroup) return

        this.feedback = ''

        if (!this.leader || !this.groupName) {
          this.feedback = 'You need to have a Group name and a Leader selected'
          return
        }

        this.isSavingGroup = true

        try {
          if (this.isLeaderOfAnotherGroup(this.leader.uid)) {
            this.feedback = this.$t('groups.messages.alreadyLeadsGroup')
            return
          }

          const selectedMembers = Array.isArray(this.selectedMemberUids)
            ? [...new Set(this.selectedMemberUids)]
            : []
          const validated = await this.validateGroupAssignments(
            this.leader.uid,
            selectedMembers
          )
          if (!validated.ok) {
            this.feedback = validated.message
            return
          }

          await db.collection('groups').add({
            cid: this.$store.getters['AppData/cid'],
            leader: this.leader.uid,
            members: validated.members.filter((uid) => uid !== this.leader.uid),
            name: this.groupName.trim(),
          })

          this.setGroupLeaderRole(this.leader.uid)
          this.resetGroupFormFields()
          this.openGroupForm = false
          this.init(this.$store.getters['AppData/cid'])
        } catch (error) {
          console.error('Error creating group:', error)
          this.feedback = this.$t('groups.messages.groupError')
        } finally {
          this.isSavingGroup = false
        }
      },
      /**
       * Give the group's leader the leader role. When a super admin changes the
       * leader of an existing group, take the role off the previous leader
       * unless they still lead another group.
       */
      setGroupLeaderRole(leaderUID, previousLeaderUID = null) {
        const userDoc = this.users.find((x) => x.uid === leaderUID)
        if (userDoc && !userDoc.roles.includes('leader')) {
          userDoc.roles = [...userDoc.roles, 'leader']
          db.collection('users')
            .doc(userDoc.user_doc_id)
            .update({ roles: userDoc.roles })
            .catch((err) => console.error('Failed to add leader role:', err))
        }

        if (!previousLeaderUID || previousLeaderUID === leaderUID) return
        const previous = this.users.find((x) => x.uid === previousLeaderUID)
        if (
          previous &&
          previous.roles.includes('leader') &&
          !this.isLeaderOfAnotherGroup(
            previousLeaderUID,
            this.groupToEdit?.group_doc_id
          )
        ) {
          previous.roles = previous.roles.filter((value) => value !== 'leader')
          db.collection('users')
            .doc(previous.user_doc_id)
            .update({ roles: previous.roles })
            .catch((err) => console.error('Failed to remove leader role:', err))
        }
      },
      removeGroupLeader(leaderUID) {
        let userDoc = this.users.find((x) => x.uid === leaderUID)
        let roles = userDoc.roles

        // console.log('1 ', userDoc, roles)

        // remove leader from the user
        if (roles.includes('leader')) {
          var filtered = roles.filter(function (value) {
            return value != 'leader'
          })

          db.collection('users')
            .doc(userDoc.user_doc_id)
            .update({
              roles: filtered,
            })
            .then(() => {})
        }
      },
      getCurrentLeader(leader) {
        this.currentLeader = leader
        // console.log('this.currentLeader ', this.currentLeader)
      },
      resetLeaderList() {
        const users = this.users
        let currentLeaderUID
        if (this.currentLeader) {
          currentLeaderUID = this.currentLeader.uid
          let newGroups = this.groups.filter(function (value) {
            if (value.uid !== currentLeaderUID) return value
            else {
              const wasLeader = users.find((u) => u.uid == value.uid)
              if (wasLeader) {
                wasLeader.disabled = false
              }
            }
          })

          this.groups = newGroups
        }

        // this backdoor is used to trigger the leaderList computed property
        this.backdoor++
      },
      getPendingUsers() {
        let pendingUsersRef = db.collection('pendingusers')
        pendingUsersRef = pendingUsersRef.where(
          'cid',
          '==',
          this.$store.getters['AppData/cid']
        )
        // pendingUsersRef.onSnapshot(snapshot => {
        pendingUsersRef.get().then((snapshot) => {
          var p = []
          snapshot.forEach((doc) => {
            let pendinguser = doc.data()
            pendinguser.id = doc.id
            p.push(pendinguser)
          })
          this.pendingusers = p
        })
      },
      // init(cid) {
      //   // console.log('init ', cid)
      //   this.groups = []
      //   this.courses = []
      //   this.allMembers = []
      //   this.isloading = true

      //   // we set a timeout for the User experience
      //   // setTimeout(() => {
      //   let groupsRef = db.collection('groups')
      //   groupsRef = groupsRef.where('cid', '==', cid)
      //   groupsRef
      //     .get()
      //     .then((snapshot) => {
      //       snapshot.forEach((doc) => {
      //         let group = doc.data()
      //         group.group_doc_id = doc.id
      //         this.groups.push(group)
      //       })
      //     })
      //     .then(() => {
      //       // grab all the members
      //       for (let i = 0; i < this.groups.length; i++) {
      //         const members = this.groups[i].members
      //         for (let m = 0; m < members.length; m++) {
      //           this.allMembers.push(members[m])
      //         }
      //       }

      //       this.users = []
      //       let usersRef = db.collection('users')
      //       usersRef = usersRef.where('cid', '==', cid)
      //       usersRef.get().then((snapshot) => {
      //         snapshot.forEach((doc) => {
      //           let user = doc.data()
      //           user.user_doc_id = doc.id

      //           console.log('ueser ', user)

      //           // Log check to debug
      //           if (!user.user_name) {
      //             console.warn(
      //               '⚠️ Missing user_name for user:',
      //               user.email || user.uid
      //             )
      //           }

      //           this.users.push(user)
      //         })
      //       })
      //     })
      //     .then(() => {
      //       let coursesRef = db.collection('courses')
      //       coursesRef = coursesRef.where('cid', '==', cid)
      //       coursesRef = coursesRef.where('archive', '==', false)
      //       coursesRef
      //         .get()
      //         .then((snapshot) => {
      //           snapshot.forEach((doc) => {
      //             let course = doc.data()
      //             course.id = doc.id
      //             this.courses.push(course)
      //           })
      //         })
      //         .then(() => {
      //           let hasCourse = this.groups.map((e) => {
      //             const courseName = this.courses.filter((item) => {
      //               if (item.selectedGroup.length > 0) {
      //                 return item.selectedGroup[0].id === e.group_doc_id
      //               }
      //             })
      //             if (courseName.length > 0) {
      //               e.hasCourse = courseName[0].selectedGroup[0].id
      //             }
      //             return e
      //           })

      //           this.groups = hasCourse
      //         })
      //     })
      //     .then(() => {
      //       this.isloading = false
      //       this.usersLoaded = true
      //     })
      //   // }, 1000)
      // },
      async init(cid) {
        this.groups = []
        this.courses = []
        this.allMembers = []
        this.users = []
        this.isloading = true
        this.usersLoaded = false

        try {
          // Load groups
          const groupsSnapshot = await db
            .collection('groups')
            .where('cid', '==', cid)
            .get()
          groupsSnapshot.forEach((doc) => {
            let group = doc.data()
            group.group_doc_id = doc.id
            this.groups.push(group)
          })

          // Collect all members
          for (let group of this.groups) {
            if (Array.isArray(group.members)) {
              this.allMembers.push(...group.members)
            }
          }

          // Load users
          const usersSnapshot = await db
            .collection('users')
            .where('cid', '==', cid)
            .get()
          usersSnapshot.forEach((doc) => {
            let user = doc.data()
            user.user_doc_id = doc.id

            // if (user.user_name) {
            //   console.log('Have user_name for user:', user.email || user.uid)
            // }

            this.users.push(user)
          })

          // Load courses
          const coursesSnapshot = await db
            .collection('courses')
            .where('cid', '==', cid)
            .where('archive', '==', false)
            .get()
          coursesSnapshot.forEach((doc) => {
            let course = doc.data()
            course.id = doc.id
            this.courses.push(course)
          })

          // Mark groups with courses
          this.groups = this.groups.map((group) => {
            const course = this.courses.find(
              (course) => course.selectedGroup?.[0]?.id === group.group_doc_id
            )
            return {
              ...group,
              hasCourse: course?.selectedGroup?.[0]?.id,
            }
          })
        } catch (error) {
          console.error('🔥 Error in init():', error)
        } finally {
          this.isloading = false
          this.usersLoaded = true
        }
      },
    },
    created() {
      this.init(this.$store.getters['AppData/cid'])

      this.$store.watch(
        () => {
          return this.$store.getters['UserData/getLoginFeedback'] // could also put a Getter here
        },
        (newValue) => {
          // console.log('newValue', newValue, 'oldValue', oldValue)
          //something changed do something
          if (newValue) {
            if (
              newValue.code &&
              (newValue.code == 'resubmit-verification' ||
                newValue.code == 'no-verfied email')
            ) {
              this.verifyEmail = true
            }
            this.feedback = newValue.message
            this.loader = null
            this.loading = false
            this.loading2 = false

            if (this.$refs.form && this.$refs.form !== undefined) {
              this.$refs.form.reset()
              this.getPendingUsers()
            }
          }
        },
        //Optional Deep if you need it
        {
          deep: true,
        }
      )

      this.getPendingUsers()
    },
  }
</script>

<style>
  .groups-container {
    margin-bottom: 25%;
  }
  .groups-container .section-heading h1 {
    display: flex;
    align-items: center;
  }
  .groups-container .users-action-bar {
    font-size: 15px;
  }
  .groups-container .groups-icon {
    margin-right: 10px;
  }
  .search-field .v-text-field__details {
    display: none;
  }
  .v-input--is-label-active label {
    font-weight: 900;
  }
  .groups-container
    .v-input:not(.v-input--is-dirty).v-select.v-select--chips:not(
      .v-text-field--single-line
    ).v-text-field--enclosed
    .v-select__selections {
    min-height: inherit;
  }
  .v-autocomplete__content {
    max-width: 705px;
  }
  .v-list-item--disabled img {
    filter: grayscale(1);
    opacity: 0.15;
  }
  .v-list-item__avatar {
    justify-content: center;
  }
  .groups-container .add-members-link {
    cursor: pointer;
  }
  .groups-container .invite-input .v-input__slot,
  .groups-container .invite-input .v-text-field__details {
    margin-bottom: 2px;
  }
  .groups-container .v-icon--disabled {
    opacity: 0.25;
  }
  svg.editGroupBtn:active {
    transform: rotate(360deg) scale(1.1);
  }
  .members-container-wrapper .v-pagination__navigation,
  .members-container-wrapper .v-pagination__item {
    box-shadow: none;
    font-size: 0.85rem;
    height: 28px;
    width: 28px;
    margin: 0.3rem;
    min-width: 28px;
  }
  .members-container-wrapper ul.v-pagination {
    margin-bottom: 0;
  }
  .member-table .v-input--checkbox .v-messages {
    display: none;
  }
  .member-table .v-input--checkbox .v-input__slot {
    margin-bottom: 0;
  }
  .dialog-header-controls {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 2;
    padding: 12px 24px;
    border-bottom: 1px solid #eee;
  }

  .dialog-scroll-list {
    max-height: 400px;
    overflow-y: auto;
    padding: 12px 24px;
  }

  .dialog-actions {
    padding: 12px 24px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    border-top: 1px solid #eee;
  }
  .member-list-container .v-checkbox .v-selection-control {
    min-height: 0px; /* or whatever height you want */
  }
  .leader-option {
    cursor: pointer;
  }
  .leader-option--disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
</style>
